import React, { useState, useEffect } from 'react';
import { Loader2, CheckCircle2, XCircle, Copy, Check, Smartphone, Download, PartyPopper } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from './ui/dialog';
import { QRCodeSVG } from 'qrcode.react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { toast } from 'sonner';

const APP_STORE_URL = 'https://apps.apple.com/us/app/pop-onboarding/id6755694268';

export default function PublishingDialog({ open, status, onClose, publishedUrl, projectId, apiKey }) {
    // status can be: 'uploading', 'success', 'error'
    const [copiedProjectId, setCopiedProjectId] = useState(false);
    const [copiedApiKey, setCopiedApiKey] = useState(false);
    const [copiedInstall, setCopiedInstall] = useState(false);
    const [copiedUsage, setCopiedUsage] = useState(false);

    // Generate the QR code value
    const qrCodeValue = JSON.stringify({ projectId, apiKey });

    const handleCopy = (text, setCopied) => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        toast.success("Copied to clipboard");
    };

    const getIcon = () => {
        switch (status) {
            case 'uploading':
                return <Loader2 className="h-16 w-16 animate-spin text-primary" />;
            case 'error':
                return <XCircle className="h-16 w-16 text-destructive" />;
            default:
                return null;
        }
    };

    const installCommand = `npm install react-native-pop-onboarding`;
    const usageCode = `import { useOnboarding, PopOnboarding } from 'react-native-pop-onboarding';
import AsyncStorage from '@react-native-async-storage/async-storage';

const App = () => {
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    checkOnboarding();
  }, []);

  const checkOnboarding = async () => {
    const hasSeen = await AsyncStorage.getItem('onboarding_complete');
    if (!hasSeen) setShowOnboarding(true);
  };

  const handleComplete = async () => {
    await AsyncStorage.setItem('onboarding_complete', 'true');
    setShowOnboarding(false);
  };

  if (!showOnboarding) return <MainApp />;

  return (
    <PopOnboarding 
      projectId="${projectId}"
      apiKey="${apiKey || 'YOUR_API_KEY'}"
      onComplete={handleComplete}
      onSkip={handleComplete}
    />
  );
};`;

    const swiftUsageCode = `import PopOnboarding
import UIKit

// Configure in AppDelegate
func application(_ application: UIApplication, didFinishLaunchingWithOptions launchOptions: [UIApplication.LaunchOptionsKey: Any]?) -> Bool {
    PopOnboarding.shared.configure(apiKey: "${apiKey || 'YOUR_API_KEY'}")
    return true
}

// Start Flow
func showOnboarding() {
    PopOnboarding.shared.startFlow(projectId: "${projectId}", from: self) { result in
        if case .success(let data) = result {
             print("Onboarding completed!")
        }
    }
}`;

    if (status === 'uploading' || status === 'error') {
        return (
            <Dialog open={open} onOpenChange={status !== 'uploading' ? onClose : undefined}>
                <DialogContent className="sm:max-w-md" hideClose={status === 'uploading'}>
                    <DialogHeader>
                        <div className="flex flex-col items-center justify-center py-4">
                            {getIcon()}
                            <DialogTitle className="mt-3 text-center text-xl">
                                {status === 'uploading' ? 'Publishing Your Flow...' : 'Publishing Failed'}
                            </DialogTitle>
                            <DialogDescription className="mt-2 text-center">
                                {status === 'uploading'
                                    ? 'Please wait while we generate and upload your onboarding flow.'
                                    : 'There was an error publishing your flow. Please try again.'}
                            </DialogDescription>
                        </div>
                    </DialogHeader>
                    {status === 'error' && (
                        <Button variant="outline" className="w-full mt-4" onClick={onClose}>
                            Close
                        </Button>
                    )}
                </DialogContent>
            </Dialog>
        );
    }

    // Success View - Split Layout
    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto p-0 gap-0">
                <div className="grid grid-cols-1 md:grid-cols-2 h-full min-h-[600px]">

                    {/* LEFT COLUMN: INTEGRATION */}
                    <div className="p-6 md:p-8 bg-background border-r flex flex-col h-full overflow-y-auto">
                        <DialogHeader className="mb-6">
                            <DialogTitle className="flex items-center gap-2 text-xl">
                                <CheckCircle2 className="h-6 w-6 text-green-500" />
                                Flow Published!
                            </DialogTitle>
                            <DialogDescription>
                                Your flow is live. Integrate it into your app now.
                            </DialogDescription>
                        </DialogHeader>

                        <div className="space-y-6">
                            {/* Credentials */}
                            <div className="space-y-4">
                                <h4 className="text-sm font-semibold border-b pb-2">1. Credentials</h4>
                                <div className="space-y-3">
                                    <div className="space-y-1.5">
                                        <Label className="text-xs">Project ID</Label>
                                        <div className="flex gap-2">
                                            <Input value={projectId} readOnly className="font-mono text-xs bg-muted h-9" />
                                            <Button variant="outline" size="icon" className="h-9 w-9 shrink-0" onClick={() => handleCopy(projectId, setCopiedProjectId)}>
                                                {copiedProjectId ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                                            </Button>
                                        </div>
                                    </div>
                                    <div className="space-y-1.5">
                                        <Label className="text-xs">API Key</Label>
                                        <div className="flex gap-2">
                                            <Input value={apiKey || '...'} type="password" readOnly className="font-mono text-xs bg-muted h-9" />
                                            <Button variant="outline" size="icon" className="h-9 w-9 shrink-0" onClick={() => handleCopy(apiKey || '', setCopiedApiKey)}>
                                                {copiedApiKey ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Installation */}
                            <div className="space-y-4">
                                <h4 className="text-sm font-semibold border-b pb-2">2. Installation</h4>
                                <Tabs defaultValue="react-native" className="w-full">
                                    <TabsList className="grid w-full grid-cols-2 mb-4">
                                        <TabsTrigger value="react-native">React Native</TabsTrigger>
                                        <TabsTrigger value="swift">Swift</TabsTrigger>
                                    </TabsList>

                                    <TabsContent value="react-native" className="space-y-4">
                                        <div className="space-y-2">
                                            <div className="flex items-center justify-between">
                                                <Label className="text-xs text-muted-foreground">Install SDK</Label>
                                                <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => handleCopy(installCommand, setCopiedInstall)}>
                                                    {copiedInstall ? <Check className="h-3 w-3 mr-1" /> : <Copy className="h-3 w-3 mr-1" />} Copy
                                                </Button>
                                            </div>
                                            <SyntaxHighlighter language="bash" style={vscDarkPlus} customStyle={{ margin: 0, borderRadius: '0.5rem', fontSize: '12px' }}>
                                                {installCommand}
                                            </SyntaxHighlighter>
                                        </div>
                                        <div className="space-y-2">
                                            <div className="flex items-center justify-between">
                                                <Label className="text-xs text-muted-foreground">Example Usage</Label>
                                                <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => handleCopy(usageCode, setCopiedUsage)}>
                                                    {copiedUsage ? <Check className="h-3 w-3 mr-1" /> : <Copy className="h-3 w-3 mr-1" />} Copy
                                                </Button>
                                            </div>
                                            <SyntaxHighlighter language="javascript" style={vscDarkPlus} customStyle={{ margin: 0, borderRadius: '0.5rem', fontSize: '12px', maxHeight: '300px' }}>
                                                {usageCode}
                                            </SyntaxHighlighter>
                                        </div>
                                    </TabsContent>

                                    <TabsContent value="swift" className="space-y-4">
                                        <div className="space-y-2">
                                            <Label className="text-xs text-muted-foreground">Add Package Dependency</Label>
                                            <div className="p-2 bg-muted rounded-md text-xs font-mono break-all border">
                                                https://github.com/jamesagib/pop-onboarding-swift
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <Label className="text-xs text-muted-foreground">Example Usage</Label>
                                            <SyntaxHighlighter language="swift" style={vscDarkPlus} customStyle={{ margin: 0, borderRadius: '0.5rem', fontSize: '12px', maxHeight: '300px' }}>
                                                {swiftUsageCode}
                                            </SyntaxHighlighter>
                                        </div>
                                    </TabsContent>
                                </Tabs>
                            </div>
                        </div>
                    </div>

                    {/* RIGHT COLUMN: PREVIEW */}
                    <div className="p-6 md:p-8 bg-muted/30 flex flex-col h-full overflow-y-auto">
                        <div className="mb-6">
                            <h3 className="text-lg font-semibold flex items-center gap-2">
                                <Smartphone className="h-5 w-5 text-primary" />
                                Preview on Device
                            </h3>
                            <p className="text-sm text-muted-foreground mt-1">
                                Instantly test your flow on a real device.
                            </p>
                        </div>

                        <div className="flex flex-col items-center gap-6 flex-1 justify-start">
                            {/* Step 1 */}
                            <div className="w-full bg-background p-4 rounded-lg border shadow-sm">
                                <div className="flex items-start gap-3">
                                    <div className="h-6 w-6 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center font-bold shrink-0 mt-0.5">1</div>
                                    <div className="flex-1">
                                        <h4 className="text-sm font-medium">Get the Pop Onboarding App</h4>
                                        <p className="text-xs text-muted-foreground mt-1 mb-3">
                                            Required to scan and preview flows.
                                        </p>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="w-full"
                                            onClick={() => window.open(APP_STORE_URL, '_blank')}
                                        >
                                            <Download className="mr-2 h-3.5 w-3.5" />
                                            Download on App Store
                                        </Button>
                                    </div>
                                </div>
                            </div>

                            {/* Step 2 */}
                            <div className="w-full bg-background p-4 rounded-lg border shadow-sm">
                                <div className="flex items-start gap-3">
                                    <div className="h-6 w-6 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center font-bold shrink-0 mt-0.5">2</div>
                                    <div className="flex-1">
                                        <h4 className="text-sm font-medium">Scan QR Code</h4>
                                        <p className="text-xs text-muted-foreground mt-1 mb-3">
                                            Open the app and tap "Scan QR".
                                        </p>
                                        <div className="flex justify-center py-2">
                                            <div className="p-3 bg-white rounded-lg border shadow-sm">
                                                <QRCodeSVG
                                                    value={qrCodeValue}
                                                    size={160}
                                                    level="M"
                                                    includeMargin={false}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-auto hidden md:block">
                                <Button variant="ghost" onClick={onClose} className="text-muted-foreground">
                                    Close & Continue Editing
                                </Button>
                            </div>
                        </div>
                    </div>

                    {/* Mobile Close Button */}
                    <div className="md:hidden p-4 border-t bg-background">
                        <Button onClick={onClose} className="w-full">Done</Button>
                    </div>

                </div>
            </DialogContent>
        </Dialog>
    );
}

